/**
 * Sequence-keyed chat message store helpers.
 *
 * Every persisted message carries a server-assigned monotonic per-chat `seq`
 * (see ChatMessage.seq). This module reconciles the four sources that can each
 * describe the same turn — REST history, replay on reconnect, the in-flight
 * snapshot (B1), and live `done` deltas — into a single ordered, de-duplicated
 * projection keyed by `seq`. There is no prefer-live / fallback-history branch:
 * sources are merged, deduped by seq, and sorted.
 *
 * Optimistic local messages (the just-sent user bubble) have no `seq` yet; they
 * are dropped once their server twin (same role + content) arrives with a seq.
 */
import type { ChatMessage } from './types';

/** Stable dedup key: server messages key by seq; optimistic ones by client id. */
export function messageStoreKey(m: ChatMessage): string {
  return typeof m.seq === 'number' ? `seq:${m.seq}` : `local:${m.id ?? ''}`;
}

/** Normalized identity for matching an optimistic message to its server twin. */
function reconcileKey(m: ChatMessage): string {
  return `${m.role}\n${(m.content ?? '').trim()}`;
}

/**
 * Combine two messages that share a store key. `next` (the later-precedence
 * source) wins field-by-field, but fields it lacks are kept from `prev` so a
 * richer earlier copy (e.g. one carrying tool_calls or attachments) is not lost.
 */
function mergeSameKey(prev: ChatMessage, next: ChatMessage): ChatMessage {
  const merged: ChatMessage = { ...prev, ...next };
  if ((!next.tool_calls || next.tool_calls.length === 0) && prev.tool_calls?.length) {
    merged.tool_calls = prev.tool_calls;
  }
  if ((!next.attachment_file_ids || next.attachment_file_ids.length === 0) && prev.attachment_file_ids?.length) {
    merged.attachment_file_ids = prev.attachment_file_ids;
  }
  if (!next.content && prev.content) merged.content = prev.content;
  return merged;
}

/**
 * Merge message lists (given in ASCENDING precedence — later lists win on
 * conflict) into one ordered, deduped projection.
 *
 * Ordering is `(timestamp, seq)` where `timestamp` is compared as epoch-ms
 * (ISO precision / timezone suffix vary, so lexical compare is unsafe). For
 * every normal message `timestamp` and `seq` are co-monotonic, so this is
 * effectively seq-ordering; `timestamp` is kept primary only so the
 * deliberately back-dated compaction summary keeps its mid-stream position
 * (its sub-millisecond back-date collapses to its neighbor's ms, then `seq`
 * tiebreaks it after that neighbor and before the next real message).
 * Optimistic messages (no seq) sort after a same-ms seq'd message.
 */
function toEpoch(ts: string | undefined): number {
  if (!ts) return Number.POSITIVE_INFINITY; // no timestamp → newest
  const t = Date.parse(ts);
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

export function mergeMessagesBySeq(...sources: ChatMessage[][]): ChatMessage[] {
  const byKey = new Map<string, ChatMessage>();
  for (const list of sources) {
    for (const m of list) {
      const key = messageStoreKey(m);
      const existing = byKey.get(key);
      byKey.set(key, existing ? mergeSameKey(existing, m) : m);
    }
  }

  const all = [...byKey.values()];

  // Reconcile: drop each optimistic (seq-less) message that has a server twin.
  // Counted as a multiset so N identical-content server messages reconcile at
  // most N optimistic twins — repeated text (e.g. two "yes" confirmations)
  // sent before their server echoes arrive isn't collapsed into one.
  const serverCounts = new Map<string, number>();
  for (const m of all) {
    if (typeof m.seq === 'number') {
      const k = reconcileKey(m);
      serverCounts.set(k, (serverCounts.get(k) ?? 0) + 1);
    }
  }
  const reconciled = all.filter((m) => {
    if (typeof m.seq === 'number') return true;
    const k = reconcileKey(m);
    const remaining = serverCounts.get(k) ?? 0;
    if (remaining > 0) {
      serverCounts.set(k, remaining - 1);
      return false; // reconciled by one server twin
    }
    return true; // no (remaining) server twin → keep optimistic
  });

  reconciled.sort((a, b) => {
    const ta = toEpoch(a.timestamp);
    const tb = toEpoch(b.timestamp);
    if (ta !== tb) return ta - tb;
    const sa = typeof a.seq === 'number' ? a.seq : Number.POSITIVE_INFINITY;
    const sb = typeof b.seq === 'number' ? b.seq : Number.POSITIVE_INFINITY;
    return sa - sb;
  });

  return reconciled;
}
